import { type LpDealPosition } from "@vega-api/generated/types/lpDealPosition";
import { MetricCard } from "@vega-core/components/MetricCard/MetricCard";
import { m } from "@vega-core/paraglide/messages";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import {
  DateFormatString,
  formatDateByPattern,
} from "@vega-core/utils/formatDate";
import { formatMoneyShort } from "@vega-core/utils/formatMoney";
import { ReportSectionMessage } from "@vega-modules/scenes/CoInvestmentsGP/pages/components/ReportSectionMessage";
import {
  formatIrr,
  formatMoic,
  formatSignedPercent,
} from "@vega-modules/scenes/CoInvestmentsGP/utils/formatFinancialReturns";

function deltaToneClass(value: number): string {
  if (value > 0) return "text-text/positive";

  if (value < 0) return "text-text/negative";

  return "text-text/secondary";
}

function PositionBody({
  position,
  isError,
  isLoading,
  onRetry,
}: {
  position: LpDealPosition | undefined;
  isError: boolean;
  isLoading: boolean;
  onRetry: () => void;
}) {
  if (isError) {
    return (
      <ReportSectionMessage
        dataTestId="lp-position-error"
        message={m.common_error_somethingWentWrong()}
        onRetry={onRetry}
      />
    );
  }

  const committedConfirmedContent = position?.committedConfirmedAt ? (
    <p className="body-small-regular text-text/secondary">
      {m.coInvestments_performanceReport_confirmed({
        date: formatDateByPattern(
          position.committedConfirmedAt,
          DateFormatString.US_DATE_WITH_COMMA,
        ),
      })}
    </p>
  ) : undefined;

  const currentValueContent =
    position?.currentValue != null ? (
      <div className="space-y-0.5">
        {position.currentValueAsOf ? (
          <p className="body-small-regular text-text/secondary">
            {m.coInvestments_performanceReport_asOf({
              date: formatDateByPattern(
                position.currentValueAsOf,
                DateFormatString.UK_DATE_SLASH,
              ),
            })}
          </p>
        ) : null}
        {position.currentValueDeltaPct != null ? (
          <p
            className={cnTwMerge(
              "body-small-regular",
              deltaToneClass(position.currentValueDeltaPct),
            )}
          >
            {m.coInvestments_performanceReport_sinceClose({
              delta: formatSignedPercent(position.currentValueDeltaPct),
            })}
          </p>
        ) : null}
      </div>
    ) : undefined;

  const totalReturnContent =
    position?.moicGross != null ? (
      <div className="space-y-0.5">
        {position.irrNet != null ? (
          <p className="body-small-regular text-text/secondary">
            {m.coInvestments_performanceReport_irrNet({
              value: formatIrr(position.irrNet),
            })}
          </p>
        ) : null}
        {position.valuationAsOf ? (
          <p className="body-small-regular text-text/secondary">
            {m.coInvestments_performanceReport_updated({
              date: formatDateByPattern(
                position.valuationAsOf,
                DateFormatString.UK_DATE_SLASH,
              ),
            })}
          </p>
        ) : null}
      </div>
    ) : undefined;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      <MetricCard
        dataTestId="lp-position-committed"
        isLoading={isLoading}
        periodContent={committedConfirmedContent}
        title={m.coInvestments_performanceReport_committedCapital()}
        value={formatMoneyShort(position?.committedCapital)}
      />
      <MetricCard
        dataTestId="lp-position-current-value"
        hasNoData={!isLoading && position?.currentValue == null}
        isLoading={isLoading}
        periodContent={currentValueContent}
        title={m.coInvestments_performanceReport_currentValue()}
        tooltip={m.coInvestments_performanceReport_currentValue_tooltip()}
        value={formatMoneyShort(position?.currentValue)}
      />
      <MetricCard
        dataTestId="lp-position-total-return"
        hasNoData={!isLoading && position?.moicGross == null}
        isLoading={isLoading}
        periodContent={totalReturnContent}
        title={m.coInvestments_performanceReport_totalReturn()}
        tooltip={m.coInvestments_performanceReport_totalReturn_tooltip()}
        value={formatMoic(position?.moicGross)}
      />
    </div>
  );
}

export function PositionSection(props: {
  position: LpDealPosition | undefined;
  isError: boolean;
  isLoading: boolean;
  onRetry: () => void;
}) {
  return (
    <div className="space-y-4">
      <h2 className="heading-5">
        {m.coInvestments_performanceReport_yourPosition()}
      </h2>
      <PositionBody {...props} />
    </div>
  );
}
